import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDatePicker from 'components/shared/CustomDatePicker/CustomDatePicker';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import dayjs from 'dayjs';
import { ClientType } from 'model/client.model';
import { Dispatch, SetStateAction } from 'react';
import zeplinColor from 'theme/Color';
import { UserType } from 'utilities/eventUtilies';
import { ProvisionalEvent } from '../../../CalendarGrid';

const REPEAT_OPTIONS: ('Weekly' | 'Daily')[] = ['Weekly', 'Daily'];

export interface DebouncedInput {
  value: string;
  debouncedValue: string;
  isValid: boolean;
  didChange: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setValue: (v: string) => void;
  clearInput: () => void;
}

export interface EventFormProps {
  type: string;
  eventName: DebouncedInput;
  eventDate: dayjs.Dayjs | null;
  setEventDate: Dispatch<SetStateAction<dayjs.Dayjs | null>>;
  initialStartTime: string;
  initialEndTime: string;
  setProvisionalEvent: Dispatch<SetStateAction<ProvisionalEvent | null>> | undefined;
  timeOptions: string[];
  clients: ClientType[];
  clientName: DebouncedInput;
  handleEditEvent: (userType: UserType) => Promise<void>;
  notes: DebouncedInput;
  isEditingExisting: boolean;
  setIsEditingExisting: Dispatch<SetStateAction<boolean>>;
  handleCreate: (userType: UserType) => Promise<void>;
  eventType: 'currentUser' | 'client';

  repeatOption?: 'Weekly' | 'Daily';
  setRepeatOption?: Dispatch<SetStateAction<'Weekly' | 'Daily'>>;
  isLoading: boolean;
}

export const EventForm = ({
  type,
  eventName,
  eventDate,
  setEventDate,
  initialStartTime,
  initialEndTime,
  setProvisionalEvent,
  timeOptions,
  clients,
  clientName,
  handleEditEvent,
  notes,
  isEditingExisting,
  setIsEditingExisting,
  handleCreate,
  eventType,
  repeatOption,
  setRepeatOption,
  isLoading,
}: EventFormProps) => {
  const isHuddle = type.toLowerCase() === 'huddle';

  return (
    <>
      <CustomTextField
        text={eventName.value}
        label={'Event name'}
        name={'event-name'}
        onChange={eventName.handleChange}
      />

      <CustomDatePicker value={eventDate} setValue={setEventDate} label='Date' />

      <Box sx={{ display: 'flex', gap: '16px' }}>
        {[
          { id: 'start', label: 'Start time', value: initialStartTime },
          { id: 'end', label: 'End time', value: initialEndTime },
        ].map((field) => (
          <Box key={field.id} sx={{ flex: 1 }}>
            <FormControl fullWidth required>
              <InputLabel
                required
                id={`${field.id}-time-label`}
                sx={{
                  color: zeplinColor.Neutral8,
                  '&.Mui-focused': { color: zeplinColor.Brand1 },
                  '& .MuiFormLabel-asterisk': { color: zeplinColor.Error },
                }}
              >
                {field.label}
              </InputLabel>
              <Select
                labelId={`${field.id}-time-label`}
                label={`${field.label} *`}
                value={field.value}
                onChange={(e) => {
                  const newValue = e.target.value as string;
                  const newTime = dayjs(newValue, 'h:mm a');

                  setProvisionalEvent &&
                    setProvisionalEvent((prev: any) => {
                      if (!prev) return null;
                      const updatedStart =
                        field.id === 'start'
                          ? prev.date.hour(newTime.hour()).minute(newTime.minute())
                          : prev.start;
                      const updatedEnd =
                        field.id === 'end'
                          ? prev.date.hour(newTime.hour()).minute(newTime.minute())
                          : prev.end;

                      return {
                        ...prev,
                        start: updatedStart,
                        end: updatedEnd,
                        duration: updatedEnd.diff(updatedStart, 'minute'),
                      };
                    });
                }}
                sx={{ borderRadius: '12px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '160px',
                      '&::-webkit-scrollbar': { width: '8px' },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: zeplinColor.Neutral5,
                        borderRadius: '10px',
                      },
                    },
                  },
                }}
              >
                {timeOptions.map((time) => (
                  <MenuItem key={`${field.id}-${time}`} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>

      {!isHuddle && setRepeatOption && (
        <FormControl>
          <InputLabel id='repeat-meeting' sx={{ color: zeplinColor.Neutral8 }}>
            Repeat meeting
          </InputLabel>
          <Select
            label='Repeat meeting'
            value={repeatOption}
            onChange={(e) => setRepeatOption(e.target.value as 'Weekly' | 'Daily')}
            sx={{ borderRadius: '12px' }}
            IconComponent={KeyboardArrowDown}
          >
            {REPEAT_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Typography variant='responsiveBody2'>{opt}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl sx={{ width: '100%' }}>
        <CustomAutocomplete
          label={isHuddle ? 'Add client' : 'Add guests'}
          options={clients || []}
          value={clients.find((c: ClientType) => c.Name === clientName.value) || null}
          getOptionLabel={(option: ClientType) => option.Name || ''}
          handleSelection={(item: ClientType) => clientName.setValue(item.Name)}
          handleDeleteItems={() => clientName.clearInput()}
          handleFilter={() => {}}
          currentLength={clients.length || 0}
          totalLength={clients.length || 0}
          next={() => {}}
          multiple={false}
        />
      </FormControl>

      <CustomTextField
        text={notes.value}
        label={isHuddle ? 'Add notes' : 'Add description'}
        name={'notes'}
        multiline
        onChange={notes.handleChange}
      />

      <CustomButton
        disabled={eventName.value === ''}
        onClick={() => {
          if (isEditingExisting) {
            handleEditEvent(eventType);
            setIsEditingExisting(false);
          } else {
            handleCreate(eventType);
          }
        }}
        variant='contained'
        color='primary'
        style={{
          borderRadius: '14px',
          height: '48px',
          width: '100%',
          backgroundColor: isLoading ? zeplinColor.Neutral4 : zeplinColor.Brand1,
        }}
        textStyle={{ color: isLoading ? zeplinColor.BasicBlack : zeplinColor.Neutral1 }}
        text={
          isEditingExisting
            ? isLoading
              ? 'Saving...'
              : 'Save Changes'
            : isHuddle
              ? isLoading
                ? 'Creating...'
                : 'Create Event...'
              : isLoading
                ? 'Saving...'
                : 'Create Meeting'
        }
      />
    </>
  );
};
